import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/features/auth/context/AuthContext';
import { 
  IoHome, 
  IoDocumentText, 
  IoSearch, 
  IoCalendar, 
  IoChatbubbles, 
  IoBarChart, 
  IoBook, 
  IoAdd, 
  IoCloudUpload, 
  IoSparkles,
  IoChevronBack,
  IoChevronForward,
  IoNotifications,
  IoSettings,
  IoPerson
} from 'react-icons/io5';
import InfoButton from '@/components/InfoButton';
import { User } from '@/types';
import './Sidebar.css';

interface SidebarProps {
  user: User | null;
  onCollapseChange?: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ user, onCollapseChange }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { logout: authLogout, startTour } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await authLogout();
      setShowProfileDropdown(false);
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      setShowProfileDropdown(false);
      navigate('/login');
    }
  };

  useEffect(() => {
    // Update CSS variable for main content margin
    document.documentElement.style.setProperty('--sidebar-width', isCollapsed ? '5rem' : '18rem');
    onCollapseChange?.(isCollapsed);
  }, [isCollapsed, onCollapseChange]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const navItems = [
    {
      icon: IoHome,
      label: 'Dashboard',
      path: '/',
      badge: null,
    },
    {
      icon: IoDocumentText,
      label: 'My applications',
      path: '/applications',
      badge: 8,
    },
    {
      icon: IoSearch,
      label: 'College Search',
      path: '/college-list-builder',
      badge: null,
    },
    {
      icon: IoCalendar,
      label: 'Deadlines',
      path: '/deadlines',
      badge: 3,
    },
    {
      icon: IoChatbubbles,
      label: 'Messages',
      path: '/messages',
      badge: 2,
    },
    {
      icon: IoBarChart,
      label: 'Progress',
      path: '/progress',
      badge: null,
    },
    {
      icon: IoBook,
      label: 'Resources',
      path: '/resources',
      badge: null,
    },
  ];

  const quickActions = [
    {
      icon: IoAdd,
      label: 'New application',
      path: '/applications/new',
    },
    {
      icon: IoCloudUpload,
      label: 'Upload document',
      path: '/upload',
    },
    {
      icon: IoSparkles,
      label: 'AI essay review',
      path: '/essay-coach',
    },
  ];

  // Enhanced active state detection
  const isActiveRoute = (path: string) => {
    if (path === '/') {
      // Only highlight dashboard when exactly on '/', not on /applications
      return location.pathname === '/';
    }
    if (path === '/essay-coach') {
      return location.pathname === '/essay-coach' || location.pathname.startsWith('/analyze');
    }
    return location.pathname.startsWith(path);
  };

  const handleNavClick = (path: string) => {
    navigate(path);
  };

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''} dark:bg-gray-800 dark:border-gray-700`}>
      <div className="sidebar-header dark:border-gray-700">
        <a 
          className="sidebar-logo-link dark:hover:bg-gray-700" 
          href="#" 
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
        >
          <div className="sidebar-logo-icon dark:bg-transparent">
            <img 
              src="/icon-new.png" 
              alt="Apply.AI Icon" 
              className="sidebar-logo-icon-img"
            />
          </div>
          {!isCollapsed && (
            <span className="sidebar-logo-text" style={{ color: '#6B7AE8' }}>APPLY.AI</span>
          )}
        </a>
      </div>

      <nav className="sidebar-nav">
        <ul className="sidebar-nav-list">
          {navItems.map((item) => {
            const IconComponent = item.icon;
            const active = isActiveRoute(item.path);
            return (
              <li key={item.path}>
                <a
                  className={`sidebar-nav-item ${active ? 'active dark:bg-blue-900/30 dark:text-blue-400' : 'dark:text-gray-300 dark:hover:bg-gray-700'}`}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(item.path);
                  }}
                >
                  <IconComponent className="sidebar-nav-icon" />
                  {!isCollapsed && (
                    <>
                      <span className="sidebar-nav-label">{item.label}</span>
                      {item.badge !== null && (
                        <span className="sidebar-nav-badge dark:bg-blue-600 dark:text-white">{item.badge}</span>
                      )}
                      {item.path === '/' && <InfoButton elementId="sidebar-dashboard" />}
                      {item.path === '/applications' && <InfoButton elementId="sidebar-my-applications" />}
                      {item.path === '/college-list-builder' && <InfoButton elementId="sidebar-college-search" />}
                      {item.path === '/deadlines' && <InfoButton elementId="sidebar-deadlines" />}
                      {item.path === '/messages' && <InfoButton elementId="sidebar-messages" />}
                      {item.path === '/progress' && <InfoButton elementId="sidebar-progress" />}
                      {item.path === '/resources' && <InfoButton elementId="sidebar-resources" />}
                    </>
                  )}
                </a>
              </li>
            );
          })}
        </ul>

        {!isCollapsed && (
          <div className="sidebar-quick-actions dark:border-gray-700">
            <p className="sidebar-quick-actions-title dark:text-gray-400">Quick Actions</p>
            <ul className="sidebar-quick-actions-list">
              {quickActions.map((action) => {
                const IconComponent = action.icon;
                const active = isActiveRoute(action.path);
                return (
                  <li key={action.path}>
                    <a
                      className={`sidebar-quick-action-item ${active ? 'active dark:bg-blue-900/30 dark:text-blue-400' : 'dark:text-gray-300 dark:hover:bg-gray-700'}`}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick(action.path);
                      }}
                    >
                      <IconComponent className="sidebar-quick-action-icon" />
                      <span className="sidebar-quick-action-label">{action.label}</span>
                      {action.path === '/applications/new' && <InfoButton elementId="sidebar-quick-action-new-application" />}
                      {action.path === '/upload' && <InfoButton elementId="sidebar-quick-action-upload-document" />}
                      {action.path === '/essay-coach' && <InfoButton elementId="sidebar-quick-action-ai-essay-review" />}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </nav>

      <div className="sidebar-footer dark:border-gray-700">
        <div className="sidebar-user-profile" ref={dropdownRef}>
          <button
            className="sidebar-user-avatar-button dark:bg-gray-700 dark:hover:bg-gray-600"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setShowProfileDropdown(!showProfileDropdown);
            }}
            type="button"
          >
            <div className="sidebar-user-avatar-icon dark:bg-blue-900/30">
              <IoPerson className="sidebar-user-avatar-icon-svg dark:text-blue-400" />
            </div>
          </button>
          {!isCollapsed && (
            <>
              <div className="sidebar-user-info">
                <p className="sidebar-user-name dark:text-gray-100">{user?.displayName || 'User'}</p>
                <p className="sidebar-user-role dark:text-gray-400">Class of 2025</p>
              </div>
              <div className="sidebar-user-actions">
                <button 
                  className="sidebar-user-action-button dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                  onClick={() => navigate('/messages')}
                  title="Notifications"
                >
                  <IoNotifications className="sidebar-user-action-icon" />
                </button>
                <button 
                  className="sidebar-user-action-button dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                  onClick={() => navigate('/settings')}
                  title="Settings"
                >
                  <IoSettings className="sidebar-user-action-icon" />
                </button>
              </div>
              {showProfileDropdown && (
                <div className="sidebar-profile-dropdown dark:bg-gray-700 dark:border-gray-600">
                  <a href="#" className="sidebar-dropdown-item dark:text-gray-200 dark:hover:bg-gray-600">Profile</a>
                  <a href="#" className="sidebar-dropdown-item dark:text-gray-200 dark:hover:bg-gray-600">Settings</a>
                  <button
                    onClick={() => {
                      setShowProfileDropdown(false);
                      navigate('/college-list-builder');
                      setTimeout(() => {
                        startTour();
                      }, 100);
                    }}
                    className="sidebar-dropdown-item dark:text-gray-200 dark:hover:bg-gray-600"
                  >
                    Show Tour
                  </button>
                  {user?.role === 'admin' && (
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setShowProfileDropdown(false);
                      }}
                      className="sidebar-dropdown-item dark:text-gray-200 dark:hover:bg-gray-600"
                    >
                      Admin Panel
                    </button>
                  )}
                  <button
                    onClick={handleLogout}
                    className="sidebar-dropdown-item sidebar-dropdown-item-logout dark:text-red-400 dark:hover:bg-gray-600"
                  >
                    Log Out
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <button
        className="sidebar-toggle dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {isCollapsed ? (
          <IoChevronForward className="sidebar-toggle-icon" />
        ) : (
          <IoChevronBack className="sidebar-toggle-icon" />
        )}
      </button>
    </div>
  );
};

export default Sidebar;

